import { isSupportedLanguage } from '@/config/languages';
import { isEnglishOnlyRole } from '@/i18n/useLocale';

/**
 * Where a signed-out person was, so the next sign-in can take her back (#2429).
 *
 * The signed-out redirect (`RequireAuth`) stashes the page; the sign-in
 * completions consume it at the moment the new session is adopted — the four
 * in `AuthContext`, and `MfaVerifyPage` for a sign-in that ends on MFA. It lives in
 * `localStorage`, not `sessionStorage`, because the sign-in that consumes it
 * is often a magic link opened in a new tab from the email.
 *
 * Three rules keep it from carrying one person's page to another on a shared
 * device:
 * - An entry names the account that left it when that is known (a mid-session
 *   expiry), and a different account never receives it.
 * - An entry is honoured for 30 minutes only.
 * - An explicit sign-out clears it and refuses new entries until the next
 *   sign-in: a deliberate sign-out never resumes.
 *
 * Every storage call is wrapped: a private window or blocked storage simply
 * means the sign-in goes to the role's home, as it did before.
 */

export const RETURN_PATH_KEY = 'trf.returnTo';
export const RETURN_PATH_TTL_MS = 30 * 60 * 1000;
/** The session-ended marker's copy in this tab's `sessionStorage` (#2429 follow-up). */
export const SESSION_ENDED_KEY = 'trf.sessionEnded';

interface Entry {
  path: string;
  account: string | null;
  at: number;
}

export interface SessionExpiry {
  account: string | null;
  lang: string | null;
}

/** The pages a sign-in must never land back on: the sign-in flow itself. */
const NEVER_RESUME = ['/login', '/register', '/forgot-password', '/reset-password', '/set-new-password', '/mfa', '/auth', '/verify-email'];

// In memory, per tab. Set by an explicit sign-out; ended by the next sign-in.
let suppressed = false;
// The mid-session expiry the signed-out redirect reports (#2429 notice and
// ?lang=). Held in memory and mirrored to this tab's `sessionStorage`, so a
// reload after the session ended still says so in her language (Victor's
// ruling, DECISIONS 2026-10-10). Tab-scoped on purpose: it never reaches
// `localStorage`, another tab, or the next browser session on the device, and
// a sign-in, a deliberate sign-out or the 30-minute window ends it.
let expiry: (SessionExpiry & { at: number }) | null = null;

function readStoredExpiry(): (SessionExpiry & { at: number }) | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_ENDED_KEY);
    if (!raw) return null;
    const e = JSON.parse(raw) as Partial<SessionExpiry & { at: number }>;
    if (typeof e?.at !== 'number') return null;
    return {
      account: typeof e.account === 'string' ? e.account : null,
      lang: typeof e.lang === 'string' ? e.lang : null,
      at: e.at,
    };
  } catch {
    return null;
  }
}

function writeStoredExpiry(e: SessionExpiry & { at: number }): void {
  try {
    window.sessionStorage.setItem(SESSION_ENDED_KEY, JSON.stringify(e));
  } catch {
    /* storage unavailable: the marker lasts until a reload */
  }
}

function forgetExpiry(): void {
  expiry = null;
  try {
    window.sessionStorage.removeItem(SESSION_ENDED_KEY);
  } catch {
    /* storage unavailable: nothing to remove */
  }
}

export function accountKeyOf(u: { id?: unknown; _id?: unknown; email?: unknown } | null | undefined): string | null {
  if (!u) return null;
  const key = u.id ?? u._id ?? u.email;
  return key == null || key === '' ? null : String(key);
}

/** A same-origin app path outside the sign-in flow. */
export function isResumablePath(path: string): boolean {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return false;
  const pathname = path.split(/[?#]/, 1)[0];
  if (pathname === '/') return false;
  return !NEVER_RESUME.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function read(): Entry | null {
  try {
    const raw = window.localStorage.getItem(RETURN_PATH_KEY);
    if (!raw) return null;
    const e = JSON.parse(raw) as Partial<Entry>;
    if (typeof e?.path !== 'string' || typeof e.at !== 'number') return null;
    return { path: e.path, account: typeof e.account === 'string' ? e.account : null, at: e.at };
  } catch {
    return null;
  }
}

function remove(): void {
  try {
    window.localStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    /* storage unavailable: nothing to remove */
  }
}

/**
 * Record `path` as where this person was. `account` is null when the redirect
 * cannot know who it was (a reload after the session ended). A re-stash of the
 * same path with no account keeps the account already recorded for it.
 */
export function stashReturnPath(path: string, account: string | null = null, now = Date.now()): void {
  if (suppressed || !isResumablePath(path)) return;
  const prev = read();
  const keep = account ?? (prev && prev.path === path && now - prev.at <= RETURN_PATH_TTL_MS ? prev.account : null);
  try {
    window.localStorage.setItem(RETURN_PATH_KEY, JSON.stringify({ path, account: keep, at: now }));
  } catch {
    /* storage unavailable: the sign-in goes home */
  }
}

/**
 * At a sign-in: the page to return to, or null for the role's home. Always
 * removes the entry, ends any sign-out suppression and drops the expiry
 * marker — a new session starts here.
 */
export function consumeReturnPath(
  user: { id?: unknown; _id?: unknown; email?: unknown } | null | undefined,
  now = Date.now(),
): string | null {
  const e = read();
  remove();
  suppressed = false;
  forgetExpiry();
  if (!e || !isResumablePath(e.path) || now - e.at > RETURN_PATH_TTL_MS || now < e.at) return null;
  if (e.account !== null && e.account !== accountKeyOf(user)) return null;
  return e.path;
}

/** An explicit sign-out: forget the page, and record none until the next sign-in. */
export function clearReturnPath(): void {
  remove();
  suppressed = true;
  forgetExpiry();
  forgetSignedInTab();
}

/**
 * The "signed in in this tab" record (#2429 round 3). The marker above is
 * written only when the open tab sees the 401; a session ended from another
 * browser and then a reload never ran that code, so /login came up in English
 * with no notice. While signed in, the tab therefore keeps who it holds and
 * in which language here — this tab's `sessionStorage` only, like the marker
 * (Victor's ruling, DECISIONS 2026-10-10). A boot whose session check fails
 * turns the record into the marker (`markSignedInTabEnded`). A deliberate
 * sign-out removes it first, so it never produces a notice.
 */
export const SIGNED_IN_TAB_KEY = 'trf.signedInTab';

/** While signed in: record (or update) who this tab holds and its language. */
export function rememberSignedInTab(info: SessionExpiry): void {
  try {
    window.sessionStorage.setItem(SIGNED_IN_TAB_KEY, JSON.stringify({ account: info.account, lang: info.lang }));
  } catch {
    /* storage unavailable: a reload after a remote sign-out shows no notice, as before */
  }
}

/** A deliberate sign-out, or the marker has taken over: drop the record. */
export function forgetSignedInTab(): void {
  try {
    window.sessionStorage.removeItem(SIGNED_IN_TAB_KEY);
  } catch {
    /* storage unavailable: nothing to remove */
  }
}

/**
 * The boot session check failed (401, refresh failed). If this tab was signed
 * in and nobody signed out deliberately, the session ended under her: write
 * the session-ended marker from the record, stamped now, and drop the record.
 * Returns whether a marker was written. A brand-new tab has no record.
 */
export function markSignedInTabEnded(now = Date.now()): boolean {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(SIGNED_IN_TAB_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;
  forgetSignedInTab();
  let rec: Partial<SessionExpiry> | null;
  try {
    rec = JSON.parse(raw) as Partial<SessionExpiry>;
  } catch {
    return false;
  }
  if (!rec || typeof rec !== 'object') return false;
  const account = typeof rec.account === 'string' ? rec.account : null;
  const lang = isSupportedLanguage(rec.lang) && rec.lang !== 'en' ? rec.lang : null;
  markSessionExpired({ account, lang }, now);
  return true;
}

/** A live session just ended under this person (a 401 the refresh could not recover). */
export function markSessionExpired(info: SessionExpiry, now = Date.now()): void {
  expiry = { account: info.account, lang: info.lang, at: now };
  writeStoredExpiry(expiry);
}

/**
 * The expiry, from memory or — after a reload — from this tab's
 * `sessionStorage`. Null once the 30-minute window has passed; a stale entry
 * is dropped.
 */
export function peekSessionExpired(now = Date.now()): SessionExpiry | null {
  const e = expiry ?? readStoredExpiry();
  if (!e) return null;
  if (now - e.at > RETURN_PATH_TTL_MS || now < e.at) {
    forgetExpiry();
    return null;
  }
  expiry = e;
  return { account: e.account, lang: e.lang };
}

/**
 * The language a signed-out page in this tab reads while the session-ended
 * marker lasts (#2429 follow-up): the redirect's `?lang=` is lost when she
 * types /login by hand, so `LocaleProvider` asks here too. Same scope as the
 * marker — this tab, 30 minutes, ended by a sign-in or a deliberate sign-out.
 */
export function sessionExpiredLanguage(now = Date.now()): string | null {
  return peekSessionExpired(now)?.lang ?? null;
}

/**
 * An anonymous language choice replaces the stored one: the marker keeps its
 * notice but stops naming a language, so her choice (`trf.lang`) governs
 * after a reload.
 */
export function forgetSessionExpiredLanguage(): void {
  const e = expiry ?? readStoredExpiry();
  if (!e || e.lang === null) return;
  expiry = { ...e, lang: null };
  writeStoredExpiry(expiry);
}

/**
 * The language the expiry redirect puts on /login (`?lang=`), so the parent
 * reads the sign-in page in her own language. (?) Lane default for #2429, not
 * a ruling: only on an expiry, never on an explicit sign-out (the next person
 * on a shared device may not read it). It is kept in this tab's
 * `sessionStorage` only, for a reload (DECISIONS 2026-10-10).
 * Staff seats are English-only (DECISIONS 2026-09-30), so they get none.
 */
export function expiryLanguageFor(
  user: { role?: string | null; preferences?: { language?: string | null } | null } | null | undefined,
): string | null {
  const lang = user?.preferences?.language;
  if (!isSupportedLanguage(lang) || lang === 'en' || isEnglishOnlyRole(user?.role)) return null;
  return lang;
}
